// deps
import { useCallback, useContext, useMemo, useState } from "react";

// internal deps
import { FlowType, ProgramSubTypes } from "@swiggy-private/super-ui";
import { VisualEffectContext } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";
// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

// constants
import { SWIGGY_BLACK_CONFETTI, SWIGGY_ONE_CONFETTI } from "@cart-client/constants/Coupon";

// helpers
import { getSubscriptionItems } from "@cart-client/helpers/Cart";

export const useOneMembershipPlanHandlers = () => {
    const [planAdded, setPlanAdded] = useState(false);

    const { cartState } = useContext(CartContext);
    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);
    const { showConfetti } = useContext(VisualEffectContext);

    const { localCart, reviewedCart: cartResponse } = cartState;
    const subscriptionItems = useMemo(() => getSubscriptionItems(cartResponse), [cartResponse]);

    // Handlers for adding and removing the plan
    const addSuperToCart = useCallback(
        async (planId: string, subscriptionNudgeInitiator?: FlowType, planType?: string) => {
            if (!updateCartInProgress) {
                let subscriptionDetails;
                const subscriptionItems = [
                    {
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        plan_id: planId,
                        quantity: 1,
                    },
                ];

                if (!isEmpty(subscriptionNudgeInitiator)) {
                    subscriptionDetails = {
                        subscriptionItems,
                        subscriptionNudgeInitiator,
                    };
                } else {
                    subscriptionDetails = {
                        subscriptionItems,
                    };
                }

                await doUpdateCart({
                    ...localCart,
                    ...subscriptionDetails,
                });
                if (!planAdded) {
                    planType === ProgramSubTypes.BLACK
                        ? showConfetti(SWIGGY_BLACK_CONFETTI)
                        : showConfetti(SWIGGY_ONE_CONFETTI);
                    setPlanAdded(true);
                }
            }
        },
        [doUpdateCart, localCart, planAdded, showConfetti, updateCartInProgress],
    );

    const removeSuperFromCart = useCallback(
        async (isMembershipPlanRemoved?: boolean) => {
            if (!updateCartInProgress && !isEmpty(subscriptionItems)) {
                let subscriptionDetails;

                if (isMembershipPlanRemoved === undefined) {
                    subscriptionDetails = {
                        subscriptionItems: [],
                    };
                } else {
                    subscriptionDetails = {
                        subscriptionItems: [],
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        is_membership_plan_removed: isMembershipPlanRemoved,
                    };
                }
                await doUpdateCart({
                    ...localCart,
                    ...subscriptionDetails,
                });
            }
        },
        [doUpdateCart, localCart, updateCartInProgress, subscriptionItems],
    );

    return { addSuperToCart, removeSuperFromCart };
};
